import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import {
  getScheduledObservations,
  getScheduledObservationsDailyStats,
  getScheduledObservationsStats,
} from "./api"
import { DateTime } from "luxon"
import { allowUpdate } from "../../app/store"

const initialState = {
  byId: {}, // A map of schedules by id
  stats: {},
  matchesByCtx: {
    // root context powering the observations list view
    root: {
      _loadStatus: "idle", // Information about the loading status of a call
      _errorMessage: undefined,
      ids: [], // scheduledObservations by id
      page: {}, // Pagination information
    },
    /*
      Example of other contexts
      "collection/123": {
        ids: [],
        page: {}, // Pagination information
      },
    */
  },
  /*
    example of _loadStatus pattern for a single object
    byId: {
      '1': {
        id: '1',
        _loadStatus: 'idle',
      }
    }
  */
}

/**
 * Thunk to perform async logic to fetch scheduled observations
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchScheduledObservations = createAsyncThunk(
  "schedules/observations/fetch",
  /**
   *
   * @param filter filters to apply to the search
   * @param page Pagination information
   * @param context
   * @returns {Promise<*>}
   */
  async ({ filter, page, context }) => {
    const response = await getScheduledObservations({
      filter,
      page,
    })
    if (context) {
      response.context = context
    }
    return response
  }
)

/**
 * Thunk to perform async logic to fetch scheduled observation stats
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchScheduledObservationStats = createAsyncThunk(
  "schedules/observations/stats/fetch",
  /**
   *
   * @param filter filters to apply to the search
   * @param context
   * @returns {Promise<*>}
   */
  async ({ filter }) => {
    const response = await getScheduledObservationsStats({
      filter,
    })
    return response
  }
)

/**
 * Thunk to perform async logic to fetch scheduled observation daily stats
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchScheduledObservationDailyStats = createAsyncThunk(
  "schedules/observations/stats/daily/fetch",
  async ({ filter }) => {
    const response = await getScheduledObservationsDailyStats({
      filter,
    })
    return response
  }
)

export const scheduledObservationsSlice = createSlice({
  name: "scheduledObservations",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    clearMatches: (state, action) => {
      state.matchesByCtx[action?.payload || "root"].ids = []
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(fetchScheduledObservations.pending, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (!state.matchesByCtx[context]) {
          state.matchesByCtx[context] = {
            ids: [],
            page: {},
          }
        }
        state.matchesByCtx[context]._loadStatus = "loading"
      })
      .addCase(fetchScheduledObservations.fulfilled, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (state.matchesByCtx[context]) {
          state.matchesByCtx[context]._loadStatus = "idle"
          // Don't reset if we are paging
          if (action.payload.page && action.payload.page.start_index === 0) {
            state.matchesByCtx[context].ids = []
          }
          // Make sure we happy to update
          if (allowUpdate(state.matchesByCtx[context].at, action.payload.at)) {
            action?.payload?.data?.map((schedule) => {
              // Add the schedule to state by id (this can include ids not retrieved in this call)
              state.byId[schedule.id] = schedule
              state.byId[schedule.id]._at = action.payload.at

              state.matchesByCtx[context].ids.push(schedule.id)
              state.matchesByCtx[context].page = action.payload.page
            })
            state.matchesByCtx[context].page = action.payload.page
            state.matchesByCtx[context].at = action.payload.at
          }
        }
      })
      .addCase(fetchScheduledObservations.rejected, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        state.matchesByCtx[context]._loadStatus = "rejected"
        state.matchesByCtx[context]._errorMessage = action?.error?.message
      })
      .addCase(fetchScheduledObservationStats.pending, (state) => {
        state.stats._loadStatus = "loading"
      })
      .addCase(fetchScheduledObservationStats.fulfilled, (state, action) => {
        state.stats._loadStatus = "idle"
        state.stats = { ...state.stats, ...action.payload }
      })
      .addCase(fetchScheduledObservationStats.rejected, (state) => {
        state.stats._loadStatus = "rejected"
      })
      .addCase(fetchScheduledObservationDailyStats.pending, (state) => {
        state.stats._loadStatus = "loading"
      })
      .addCase(
        fetchScheduledObservationDailyStats.fulfilled,
        (state, action) => {
          state.stats._loadStatus = "idle"
          state.stats.daily_observations = action.payload.daily_observations
        }
      )
      .addCase(fetchScheduledObservationDailyStats.rejected, (state) => {
        state.stats._loadStatus = "rejected"
      })
  },
})

// Selector for all scheduled observations
export const selectScheduledObservationsById = (state) =>
  state.scheduledObservations.byId

// Selector for scheduled observation stats
export const selectScheduledObservationsStats = (state) =>
  state.scheduledObservations.stats

/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectScheduledObservationsByContext(state, `schedule/${id}`))
 */
export const selectScheduledObservationsByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.scheduledObservations.matchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`scheduledObservations, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectScheduledObservationsByIds(state, ["1", "2"]))
 */
export const selectScheduledObservationsByIds = createSelector(
  [
    // first input - extract value from `state`
    selectScheduledObservationsById,
    // Take the second arg, `ids`, and forward to the output selector
    (state, ids) => ids,
  ],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)

/** splitAroundActive takes an array of scheduled observations, and
 * splits it into three arrays, returned in an object as:
 * {upcoming: [], active: [], past: []}
 * It does not re-order the items, and it's recommended to start
 * with an array that is already in ascending order by time ("ASC").
 *
 * Splicing logic: If we consider the current time 16, and for
 * an original list of observations with time stamps of:
 * [11,12,13,14,15,16,16,17,18,18,19] the resulting
 * collections look like:
 * {
 *  upcoming: [17, 18, 18, 19],
 *  active: [16, 16],
 *  past: [15, 14, 13, 12, 11]
 * }
 * Such that the items "closest to now" are at the top of the
 * upcoming and past collections.
 */
export const splitAroundActive = (schedObs = []) => {
  // Note: Because _active is a server-side concept, this function
  // cannot be a pure function which takes in the current time, as it
  // would not correspond to the server's _active flag. Instead, we
  // ideally require a server-side service that also indicates
  // whether scheduled observations are _upcoming or _past (for example).
  const now = DateTime.now()
  const spl = { upcoming: [], active: [], past: [] }
  if (!schedObs || schedObs.length < 1) {
    return spl
  }
  schedObs.forEach((o) => {
    const d = DateTime.fromISO(o.date)
    if (o._active) {
      spl.active.push(o)
    } else if (!d._active && d > now) {
      spl.upcoming.push(o)
    } else if (!d._active && d <= now) {
      spl.past.push(o)
    }
  })
  spl.past = spl.past.reverse()
  return spl
}

export default scheduledObservationsSlice.reducer
